import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getApiWebhookDashboard } from "@/lib/api-keys.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { Activity, CheckCircle2, XCircle, Webhook, KeyRound, RefreshCw, AlertTriangle, LayoutDashboard, ListChecks } from "lucide-react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";

export const Route = createFileRoute("/_authenticated/admin/api-dashboard")({
  component: ApiDashboardPage,
});

function fmt(n: number) {
  return n.toLocaleString();
}

function timeAgo(iso: string | null) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function ApiDashboardPage() {
  const fn = useServerFn(getApiWebhookDashboard);
  const q = useQuery({
    queryKey: ["api-webhook-dashboard"],
    queryFn: () => fn(),
    refetchInterval: 30_000,
  });

  const d = q.data as any;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="h-5 w-5" /> API & webhooks dashboard
          </h2>
          <p className="text-sm text-muted-foreground">
            Delivery health, recent activity, and quota usage. Auto-refreshes every 30s.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => q.refetch()} disabled={q.isFetching}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${q.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {q.isLoading && (
        <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>
      )}

      {d && (
        <Tabs defaultValue="overview" className="w-full">
          <PillTabsList search>
            <PillTabsTrigger value="overview"><LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />Overview</PillTabsTrigger>
            <PillTabsTrigger value="endpoints"><Webhook className="h-3.5 w-3.5 mr-1.5" />Endpoints</PillTabsTrigger>
            <PillTabsTrigger value="recent"><ListChecks className="h-3.5 w-3.5 mr-1.5" />Recent attempts</PillTabsTrigger>
            <PillTabsTrigger value="keys"><KeyRound className="h-3.5 w-3.5 mr-1.5" />API key usage</PillTabsTrigger>
          </PillTabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-4 space-y-6">
            <div className="grid gap-4 md:grid-cols-4">
              <KpiCard
                icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                label="Success rate (24h)"
                value={`${d.summary24h.rate}%`}
                hint={`${fmt(d.summary24h.ok)} of ${fmt(d.summary24h.total)} delivered`}
              />
              <KpiCard
                icon={<XCircle className="h-4 w-4 text-destructive" />}
                label="Failures (24h)"
                value={fmt(d.summary24h.fail)}
                hint={`${fmt(d.summary24h.pend)} pending`}
              />
              <KpiCard
                icon={<Webhook className="h-4 w-4 text-blue-500" />}
                label="Endpoints"
                value={`${d.totals.active_endpoints}/${d.totals.endpoints}`}
                hint="Active / total"
              />
              <KpiCard
                icon={<KeyRound className="h-4 w-4 text-violet-500" />}
                label="API keys"
                value={`${d.totals.active_keys}/${d.totals.keys}`}
                hint="Active / total"
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Deliveries (last 24h)</CardTitle>
                <CardDescription>Hourly success vs failure counts.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.buckets}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 6,
                          fontSize: 12,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="ok" name="Success" stackId="a" fill="hsl(var(--primary))" />
                      <Bar dataKey="fail" name="Failed" stackId="a" fill="hsl(var(--destructive))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  7-day totals: {fmt(d.summary7d.ok)} success · {fmt(d.summary7d.fail)} failed ·{" "}
                  <strong>{d.summary7d.rate}%</strong> success rate
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Endpoints */}
          <TabsContent value="endpoints" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Endpoint health (24h)</CardTitle>
                <CardDescription>Success rate per outbound webhook endpoint.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Endpoint</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Success</TableHead>
                      <TableHead className="text-right">Fail</TableHead>
                      <TableHead className="w-40">Success rate</TableHead>
                      <TableHead>Last success</TableHead>
                      <TableHead>Last failure</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.endpointStats.map((e: any) => (
                      <TableRow key={e.id}>
                        <TableCell className="max-w-[280px]">
                          <div className="font-medium text-sm">{e.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{e.url}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={e.active ? "default" : "secondary"}>
                            {e.active ? "Active" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm">{fmt(e.ok)}</TableCell>
                        <TableCell className="text-right text-sm">{fmt(e.fail)}</TableCell>
                        <TableCell>
                          {e.total === 0 ? (
                            <span className="text-xs text-muted-foreground">No traffic</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Progress value={e.rate ?? 0} className="h-1.5" />
                              <span className="text-xs tabular-nums w-10 text-right">{e.rate}%</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {timeAgo(e.last_success_at)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {e.failure_count > 0 && (
                            <AlertTriangle className="inline h-3 w-3 mr-1 text-destructive" />
                          )}
                          {timeAgo(e.last_failure_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {d.endpointStats.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                          No endpoints configured.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent attempts */}
          <TabsContent value="recent" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent attempts</CardTitle>
                <CardDescription>Latest 25 delivery attempts across all endpoints.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Endpoint</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>HTTP</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.recentDeliveries.map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-xs">{r.endpoint_name}</TableCell>
                        <TableCell className="text-xs font-mono">{r.event}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              r.status === "success"
                                ? "default"
                                : r.status === "pending"
                                  ? "secondary"
                                  : "destructive"
                            }
                          >
                            {r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{r.response_status ?? "—"}</TableCell>
                        <TableCell className="text-xs">{r.attempts ?? 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">
                          {r.error ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {d.recentDeliveries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                          No deliveries yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API key usage */}
          <TabsContent value="keys" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">API key usage</CardTitle>
                <CardDescription>
                  Rate-limit usage (last minute) and daily quota usage (last 24h).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Key</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-56">Rate (per min)</TableHead>
                      <TableHead className="w-56">Daily quota</TableHead>
                      <TableHead>Last used</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.apiKeyUsage.map((k: any) => (
                      <TableRow key={k.id}>
                        <TableCell>
                          <div className="font-medium text-sm">{k.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{k.prefix}…</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{k.kind}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={k.status === "active" ? "default" : "secondary"}>
                            {k.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={k.rate_pct} className="h-1.5" />
                            <span className="text-xs tabular-nums whitespace-nowrap">
                              {k.used_last_minute}/{k.rate_limit_per_minute}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {k.quota_per_day == null ? (
                            <span className="text-xs text-muted-foreground">Unlimited</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Progress value={k.quota_pct ?? 0} className="h-1.5" />
                              <span className="text-xs tabular-nums whitespace-nowrap">
                                {fmt(k.used_last_24h)}/{fmt(k.quota_per_day)}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {timeAgo(k.last_used_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {d.apiKeyUsage.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">
                          No API keys yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function KpiCard({
  icon, label, value, hint,
}: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon} {label}</div>
        <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
        {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}
